import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { useApiContext } from "../../api/context";
import { useCustomerContext } from "../../Context/CustomerContext";
import { type Organization } from "../types";

/**
 * Hook to manage Zendesk organization data and operations
 * @returns Object containing organization query data and toggle mutation function
 */
export function useZendeskOrganization() {
  const api = useApiContext();
  const { customer } = useCustomerContext();
  const queryClient = useQueryClient();

  const organizationQuery = useQuery<Organization, AxiosError>({
    queryKey: ["support", "organization", customer.id],
    queryFn: async () => {
      const response = await api.request({
        method: "get",
        url: `/support/customers/${customer.id}/users`,
      });

      return response?.data?.organization as Organization;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: Boolean(customer?.id),
  });

  const toggleSharingMutation = useMutation<Organization, AxiosError, { currentSharedTickets: boolean }>({
    mutationFn: async ({ currentSharedTickets }) => {
      const nextValue = !currentSharedTickets;
      const response = await api.request({
        method: "put",
        data: {
          organization: {
            shared_tickets: nextValue,
            shared_comments: nextValue,
          },
        },
        url: `/support/customers/${customer.id}/sharing`,
      });
      return response?.data?.organization as Organization;
    },
    onSuccess: () => {
      // Invalidate and refetch organization data after successful toggle
      queryClient.invalidateQueries({
        queryKey: ["support", "organization", customer.id],
      });
    },
  });

  return {
    // Organization data
    data: organizationQuery.data,
    isLoading: organizationQuery.isLoading,
    error: organizationQuery.error,
    isError: organizationQuery.isError,

    // Toggle sharing mutation
    toggleOrganizationSharing: (organization: Organization) => {
      toggleSharingMutation.mutate({ currentSharedTickets: organization.shared_tickets });
    },
    toggleError: toggleSharingMutation.error,
  };
}
